import { RetweetOutlined } from '@ant-design/icons';
import { Icons } from '@nxsys/types/icons';
import AppButton from '@nxsys/components/AppButton';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppEnums';
import {
  getIrelandPaymentModelOptions,
  getIrelandPaymentModels,
} from '@nxsys/constants/AppConstants';
import { useConfirmPayment } from '@nxsys/hooks/api/dashboard';
import { usePerformReversalPayrolls } from '@nxsys/hooks/api/timesheets';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import useNotificationSocket from '@nxsys/hooks/useNotificationSocket';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND, UNITED_KINGDOM } from '@nxsys/store/constants/keys';
import { dashboardActions } from '@nxsys/store/slices/dashboard/reducer/dashboardSlice';
import { GetPaymentFileListParams } from '@nxsys/store/slices/dashboard/types';
import { PerformReversalPayrollsParams } from '@nxsys/types/api';
import { Option } from '@nxsys/types/models/Common';
import { ICompany } from '@nxsys/types/models/company';
import { IPayroll } from '@nxsys/types/models/payroll';
import { getAppConfig, showCurrencyByCountry } from '@nxsys/utils';
import { useViewPayslipById } from '@nxsys/hooks/api/payroll/usePayrollApi';
import { showMessage } from '@nxsys/utils/message';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { debounce } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TimesheetReverseModal from '../../timesheets/components/TimesheetReverseModal';
import { useCanReversePayroll } from '@nxsys/hooks/useCanReversePayroll';
import SubmitFpsPsrPopup from './SubmitFpsPsrPopup';

type Props = {
  isOpen: boolean;
  toggle: () => void;
  cbAfterAction?: () => void;
  // Opening the FPS/PSR screen is left to the parent: this modal is rendered on
  // both the admin and the employer dashboard, and only the parent knows which
  // submission screen is available there.
  onSubmitFpsPsr?: () => void;
};

const ConfirmPaymentModal: React.FC<Props> = ({
  isOpen,
  toggle,
  cbAfterAction = () => {},
  onSubmitFpsPsr = () => {},
}) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch();
  const navigate = useNavigateWithQuery();
  const paymentModels = getAppConfig('paymentModels');
  const { payrollCountry, user, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const canReverse = useCanReversePayroll();
  const isUK = isBureau && payrollCountry === UNITED_KINGDOM;
  // Country-only: an umbrella-Ireland tenant also needs the Ireland
  // payment-model filter below (with UMBRELLA added to it), not the
  // unfiltered UK-umbrella list it fell through to before.
  const isIreland = payrollCountry === IRELAND;
  const { paymentFileList } = useSelector(
    (state: AppState) => state.dashboardStore,
  );
  const { list, pagination, loading, queryParams } = paymentFileList;
  const [selectedRows, setSelectedRows] = useState<IPayroll[] | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const [selectedPaymentModel, setSelectedPaymentModel] =
    useState<Option<string> | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<ICompany | null>(null);
  const [selectedAgency, setSelectedAgency] = useState<IAgency[] | null>(null);
  // The filter inputs keep their own internal value, so bumping this key on
  // close remounts them and stops them from displaying a selection we just
  // cleared.
  const [filtersKey, setFiltersKey] = useState(0);

  const clearSelectedRows = () => {
    setSelectedRows(null);
    setSelectedRowKeys([]);
  };

  const [reverseModal, setReverseModal] = useState<{
    isOpen: boolean;
    payrollIds: string[];
  }>({
    isOpen: false,
    payrollIds: [],
  });
  const [showFpsPsrPrompt, setShowFpsPsrPrompt] = useState(false);
  const confirmedHasPayeOrUmbrellaRef = useRef(false);
  const toggleReverseModal = () => {
    setReverseModal((prev) => ({
      ...prev,
      isOpen: !prev.isOpen,
    }));
  };

  // This modal stays mounted between openings, so nothing resets itself and a
  // selection made under a previous set of filters would still be sitting there
  // ready to be confirmed.
  useEffect(() => {
    if (isOpen) return;
    clearSelectedRows();
    setSelectedPaymentModel(null);
    setSelectedCompany(null);
    setSelectedAgency(null);
    setFiltersKey((key) => key + 1);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      // Rows picked under the previous filters are not in the incoming list
      clearSelectedRows();
      handleFetchData({
        page: 0,
        size: 20,
        paymentModels: selectedPaymentModel?.value
          ? [selectedPaymentModel.value]
          : undefined,
        companyPaymentId: selectedCompany?.id ?? undefined,
        agencyRefs: selectedAgency?.map((item: any) => item.id) ?? undefined,
      });
    }
  }, [isOpen, selectedPaymentModel, selectedCompany, selectedAgency]);

  const handleFetchData = (params: GetPaymentFileListParams) => {
    const newParams = {
      ...params,
      isUnconfirmed: true,
    };
    if (isUK) {
      newParams.paymentModels = [PAYMENT_MODEL_ENUM.PAYEE];
    }
    if (isIreland) {
      // Allow-list, not an override: honour the user's pick when they made
      // one, otherwise fall back to every model this tenant can hold. The
      // unconditional assignment predates umbrella-Ireland and was harmless
      // while isIreland implied bureau, where no picker is rendered.
      const allowed = getIrelandPaymentModels(isBureau);
      const picked = params.paymentModels?.filter((m) =>
        allowed.includes(m as PAYMENT_MODEL_ENUM),
      );
      newParams.paymentModels = picked?.length ? picked : allowed;
    }
    dispatch(dashboardActions.getPaymentFileList(newParams));
  };

  const refetchData = () => {
    handleFetchData({ ...queryParams });
    cbAfterAction();
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      clearSelectedRows();
      handleFetchData({ ...queryParams, search: e.target.value });
    }, 500),
    [queryParams],
  );

  const handleView = useCallback(
    (id: string) => {
      navigate(`/reports/payroll/${id}`, {
        query: {
          from: '/dashboards',
        },
      });
    },
    [navigate],
  );

  const { run: viewPayslipById } = useViewPayslipById();
  // The stored s3Url is empty whenever an earlier generation or upload failed.
  // This endpoint returns the existing file when there is one and generates,
  // uploads and persists it when there is not, so the download heals itself
  // rather than telling the user to regenerate from a screen with no such
  // control.
  const handleDownloadPayslip = useCallback(
    (selfBillId?: string) => {
      if (!selfBillId) {
        showMessage({
          type: 'error',
          content: 'No payslip exists for this payroll yet.',
        });
        return;
      }
      viewPayslipById(selfBillId).then((res) => {
        if (res?.success && res.data) {
          window.open(res.data, '_blank');
        }
      });
    },
    [viewPayslipById],
  );

  const actionsColumn: ColumnsType<IPayroll>[0] = {
    key: 'actions',
    title: 'Actions',
    fixed: 'left',
    align: 'left',
    render: (record) => (
      <ul className="flex items-center justify-start gap-x-4">
        <li>
          <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 8, alignItems: 'center' }}>
            <Tooltip title="View">
              <span className="cursor-pointer" onClick={() => handleView(record.id)}>
                <Icons.IconEye />
              </span>
            </Tooltip>
            <Tooltip title="Download">
              <span
                className="cursor-pointer"
                onClick={() => handleDownloadPayslip(record?.selfBillRef?.id)}
              >
                <Icons.IconDownload />
              </span>
            </Tooltip>
            {canReverse && (
              <Tooltip title="Reversal Payroll">
                <span className="cursor-pointer" onClick={() => handleOpenReverse([record])}>
                  <RetweetOutlined style={{ color: 'orange' }} />
                </span>
              </Tooltip>
            )}
          </div>
        </li>
        {record?.isPAL && (
          <li>
            <Tag color="warning" className="ml-1">PAL</Tag>
          </li>
        )}
      </ul>
    ),
    width: 150,
  };

  const bureauColumns: ColumnsType<IPayroll> = [
    actionsColumn,
    {
      key: 'id',
      title: 'ID',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (record) => <span>{record?.id || '_'}</span>,
    },
    {
      key: 'subcontractorRef',
      title: 'Employee ID',
      dataIndex: 'subcontractorRef',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => value?.id || '_',
    },
    {
      key: 'subcontractorName',
      title: 'Employee Name',
      dataIndex: 'subcontractorName',
    },
    {
      key: 'companyRef',
      title: 'Employer Name',
      dataIndex: 'companyRef',
      render: (value) => value?.companyName || '_',
    },
    {
      key: 'net',
      title: 'Net Pay',
      dataIndex: 'net',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
    },
    {
      key: 'accountName',
      title: 'Account Name',
      dataIndex: 'subcontractorRef',
      render: (value) => value?.bankRef?.accountName || '_',
    },
    {
      key: 'accountNumber',
      title: 'Account Number',
      dataIndex: 'subcontractorRef',
      render: (value) => value?.bankRef?.accountNumber || '_',
    },
    {
      key: 'sortCode',
      title: 'Sort Code',
      dataIndex: 'subcontractorRef',
      render: (value) => value?.bankRef?.sortCode || '_',
    },
  ];

  const umbrellaColumns: ColumnsType<IPayroll> = [
    actionsColumn,
    {
      key: 'id',
      title: 'ID',
      width: 160,
      render: (record) => <span>{record?.id || '_'}</span>,
    },
    {
      key: 'taxPeriod',
      title: 'Tax Period',
      align: 'right',
      render: (record) =>
        `${record?.taxPeriod || ''}/${
          record?.taxYear || record?.currentYear || ''
        }`,
      sorter: true,
      width: 160,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'subcontractorRef',
      title: 'Employee ID',
      dataIndex: 'subcontractorRef',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => value?.id || '_',
      width: 160,
    },
    {
      key: 'subcontractorRef',
      title: 'Agency Ref',
      dataIndex: 'subcontractorRef',
      render: (value) =>
        `${value?.primaryAgency?.agencyPrefix || '___'}/${
          value?.agencyRef || value?.systemId || '_'
        }`,
      width: 250,
    },
    {
      key: 'agencyName',
      title: 'Agency Name',
      dataIndex: 'agencyName',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      width: 350,
    },
    {
      key: 'subcontractorName',
      title: 'Employee Name',
      width: 200,
      render: (value) =>
        value?.subcontractorName ||
        `${value?.subcontractorRef?.forename || ''} ${
          value?.subcontractorRef?.surname || ''
        }`,
    },
    {
      key: 'timesheetValue',
      title: 'Timesheet Value',
      dataIndex: 'timesheetValue',
      align: 'right',
      width: 200,
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
    },
    {
      key: 'margin',
      title: 'Margin',
      dataIndex: 'margin',
      align: 'right',
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
      sorter: true,
      width: 150,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'gross',
      title: 'Gross',
      dataIndex: 'gross',
      align: 'right',
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
      sorter: true,
      width: 150,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'materials',
      title: 'Materials',
      dataIndex: 'materials',
      align: 'right',
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
      sorter: true,
      width: 150,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'vat',
      title: 'VAT',
      dataIndex: 'vat',
      align: 'right',
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      width: 150,
    },
    {
      key: 'net',
      title: 'Net',
      dataIndex: 'net',
      align: 'right',
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
      sorter: true,
      width: 150,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'cisStatus',
      title: 'CIS Status',
      dataIndex: 'cisStatus',
      align: 'center',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'cisDeduction',
      title: 'CIS Deduction (amount)',
      dataIndex: 'cisDeduction',
      align: 'right',
      width: 250,
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'otherDeduction',
      title: 'Other Deduction',
      dataIndex: 'otherDeduction',
      align: 'right',
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'paymentDate',
      title: 'Payment Date',
      dataIndex: 'paymentDate',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '',
    },
    {
      key: 'invoiceNo',
      title: 'Sales Inv Ref',
      dataIndex: 'invoiceNo',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
  ];

  const columns = isBureau ? bureauColumns : umbrellaColumns;

  useNotificationSocket({
    eventName: `confirm-payments-${user?.id}`,
    onSuccess: () => {
      showMessage({
        type: 'success',
        content: 'Confirm payment successfully.',
      });
      refetchData();
      window.dispatchEvent(new CustomEvent('dashboard:reload-rti'));
      // NX-5676: a confirmed payment sets paymentDate, which is what the
      // recurring-payment/deduction alert counts key on, so refresh them too.
      window.dispatchEvent(new CustomEvent('dashboard:reload-actionRequired'));
      if (
        confirmedHasPayeOrUmbrellaRef.current &&
        (payrollCountry === UNITED_KINGDOM ||
          payrollCountry === IRELAND)
      ) {
        setShowFpsPsrPrompt(true);
      }
      confirmedHasPayeOrUmbrellaRef.current = false;
    },
    onError: () => {
      confirmedHasPayeOrUmbrellaRef.current = false;
      showMessage({
        type: 'error',
        content: 'Confirm payment failed.',
      });
    },
  });
  const { run: confirmPayment, loading: isConfirmPaymentLoading } =
    useConfirmPayment();
  const handleConfirmPayment = async () => {
    if (!selectedRows?.length) {
      showMessage({
        type: 'error',
        content: 'Please select at least one payroll to confirm.',
      });
      return;
    }
    const res = await confirmPayment({
      ids: selectedRows.map((item) => item.id),
      paymentDate: new Date().toISOString(),
    });
    if (res.success) {
      const payeUmbrellaPaymentModels: string[] =
        getAppConfig('payeUmbrellaPaymentModels') ?? [];
      confirmedHasPayeOrUmbrellaRef.current = selectedRows.some((row) =>
        payeUmbrellaPaymentModels.includes(row.paymentModel ?? ''),
      );
      showMessage({
        type: 'info',
        content: 'The selected payrolls are being confirmed.',
      });
      clearSelectedRows();
    }
  };

  useNotificationSocket({
    eventName: `reversal-payroll-${user?.id}`,
    onSuccess: () => {
      showMessage({
        type: 'success',
        content: 'Reversal payroll successfully.',
      });
      refetchData();
      window.dispatchEvent(new CustomEvent('dashboard:reload-payrollImport'));
    },
    onError: () => {
      showMessage({
        type: 'error',
        content: 'Reversal payroll failed.',
      });
    },
  });
  useNotificationSocket({
    eventName: `reversal-payroll-from-timesheet-${user?.id}`,
    onSuccess: () => {
      showMessage({
        type: 'success',
        content: 'Reversal payroll successfully.',
      });
      refetchData();
      window.dispatchEvent(new CustomEvent('dashboard:reload-payrollImport'));
    },
    onError: () => {
      showMessage({
        type: 'error',
        content: 'Reversal payroll failed.',
      });
    },
  });
  const {
    run: performReversalPayrolls,
    loading: isPerformReversalPayrollsPending,
  } = usePerformReversalPayrolls();
  const handleReverse = async (params: PerformReversalPayrollsParams) => {
    await performReversalPayrolls(params).then(() => {
      showMessage({
        type: 'info',
        content: messages['timesheet.reverseSuccessPending'],
      });
    });
  };
  const handleOpenReverse = (data: IPayroll[]) => {
    setReverseModal((prev) => ({
      ...prev,
      isOpen: true,
      payrollIds: data.map((item) => item.id),
    }));
  };
  const handleOpenReverseSelected = () => {
    if (!selectedRows || selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleOpenReverse(selectedRows);
  };

  return (
    <AppModalContainer
      title={
        <div className="flex items-center justify-between w-full gap-x-[8px]">
          <div className="whitespace-nowrap">Confirm Payment</div>
          <div className="flex gap-x-[8px]">
            <AppButton
              name="Confirm"
              loading={isConfirmPaymentLoading}
              disabled={isConfirmPaymentLoading}
              onClick={handleConfirmPayment}
            />
            {canReverse && (
              <AppButton
                variant="outlined"
                name="Reverse Payroll"
                loading={isPerformReversalPayrollsPending}
                disabled={isPerformReversalPayrollsPending}
                onClick={handleOpenReverseSelected}
              />
            )}
          </div>
        </div>
      }
      isOpen={isOpen}
      toggle={toggle}
      hiddenFooter
      className="max-sm:w-[100vw]"
      width={1200}
      minHeight="0"
      noPaddingBody
      titleFullWidth
    >
      <AppsContent>
        <div className="flex items-center justify-between gap-2 w-full my-[20px] px-[10px] flex-wrap">
          <div className="flex items-center gap-2 flex-1 flex-wrap">
            {!isBureau && (
              <AppSelect
                key={`payment-model-${filtersKey}`}
                options={
                  (isIreland
                    ? getIrelandPaymentModelOptions(isBureau)
                    : paymentModels) as any
                }
                placeholder="Payment Model"
                className="!min-w-0 !w-fit"
                onChange={(value) => setSelectedPaymentModel(value)}
              />
            )}
            <AppSelectSync
              key={`company-${filtersKey}`}
              type="company"
              apiType="searchCompany"
              placeholder={
                isBureau ? 'Select Employer' : 'Select Payment Company'
              }
              onChangeValue={(value: any) => {
                setSelectedCompany(value?.fullData);
              }}
              paramFilter={
                isUK ? { paymentModalEmployee: PAYMENT_MODEL_ENUM.PAYEE } : {}
              }
            />
            {!isBureau && (
              <AppSelectSync
                key={`agency-${filtersKey}`}
                type="agency"
                apiType="searchAgency"
                placeholder="Select Agency"
                mode="multiple"
                onChangeValue={(value: any) => {
                  setSelectedAgency(value?.map((item: any) => item.fullData));
                }}
                paramFilter={{ hasPaymentFileNeedConfirm: true }}
                className="!min-w-[165px]"
              />
            )}
          </div>
          <div className="flex items-center gap-2">
            <AppInputSearch
              key={`search-${filtersKey}`}
              onChange={handleSearch}
            />
          </div>
        </div>
        <AppTableContainer
          rowKey="id"
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: isBureau ? 1900 : 3500 }}
          columns={columns}
          onChangePagination={onChangePagination}
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys,
            onChange: (keys: React.Key[], selectedRows: IPayroll[]) => {
              setSelectedRowKeys(keys);
              setSelectedRows(selectedRows);
            },
          }}
        />
      </AppsContent>
      <TimesheetReverseModal
        isOpen={reverseModal.isOpen}
        toggle={toggleReverseModal}
        payrollIds={reverseModal.payrollIds}
        onPerform={handleReverse}
      />
      <SubmitFpsPsrPopup
        isOpen={showFpsPsrPrompt}
        country={payrollCountry === IRELAND ? 'IE' : 'UK'}
        onYes={() => {
          setShowFpsPsrPrompt(false);
          toggle();
          onSubmitFpsPsr();
        }}
        onNo={() => setShowFpsPsrPrompt(false)}
      />
    </AppModalContainer>
  );
};

export default ConfirmPaymentModal;
